
/*
 * VNCcontact+ : A new level of contact management
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import * as fromGroupContacts from "../actions/group-contacts.action";
import { createEntityAdapter, EntityAdapter, EntityState } from "@ngrx/entity";
import { Action } from "../../../actions";
import { Contact } from "src/app/common/models";

export interface ContactGroupState extends EntityState<Contact> {
  isLoaded: boolean;
  isLoading: boolean;

  isNextPageLoading: boolean;
  isNextPageLoaded: boolean;
  currentOffset: number;
  isMoreContacts: boolean;

  selectedContactsIds: number[];
  isSearchMode: boolean;
  queryParams: any;
}

export const contactGroupAdapter: EntityAdapter<Contact> = createEntityAdapter<Contact>({
  selectId: (contact: Contact) => contact.id
});

export function sortByFirstCharacter(contact1: Contact, contact2: Contact): number {
  return contact1.fullName.charCodeAt(0) - contact2.fullName.charCodeAt(0);
}

export const initialState: ContactGroupState = contactGroupAdapter.getInitialState({
  isLoading: false,
  isLoaded: false,

  isNextPageLoading: false,
  isNextPageLoaded: false,
  currentOffset: 0,
  isMoreContacts: false,

  selectedContactsIds: [],
  sortBy: null,
  isSearchMode: false,
  queryParams : null
});

export function groupContactReducer(
  state = initialState,
  action: Action
): ContactGroupState {
  switch (action.type) {
    case fromGroupContacts.GroupContactsAcTypes.LOAD_GROUP_CONTACTS: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.LOAD_GROUP_CONTACTS_SUCCESS: {
      return contactGroupAdapter.addAll(action.payload.contact, {
        ...state,
        isLoading: false,
        isLoaded: true,
        currentOffset: action.payload.currOffset,
        isMoreContacts: action.payload.isMoreContacts
      });
    }

    case fromGroupContacts.GroupContactsAcTypes.LOAD_GROUP_CONTACTS_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.NEXT_LOAD_GROUP_CONTACTS: {
      return {
        ...state,
        isNextPageLoading: true,
        isLoading: true
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.NEXT_LOAD_GROUP_CONTACTS_SUCCESS: {
      const newState = contactGroupAdapter.addMany(action.payload.contacts, {
        ...state,
        isNextPageLoading: false,
        isNextPageLoaded: true,
        isLoading: false,
        isLoaded: true,
        currentOffset: action.payload.currOffset,
        isMoreContacts: action.payload.isMoreContacts
      });

      const changes = action.payload.contacts.map(contact => {
        return {id: contact.id, changes: contact };
      });
      return contactGroupAdapter.updateMany(changes, newState);
    }

    case fromGroupContacts.GroupContactsAcTypes.EMPTY_GROUP_CONTACTS_SUCCESS: {
      const newState = contactGroupAdapter.removeAll( {
        ...state,
        isLoading: false,
        isLoaded: true,
        currentOffset: 0,
        isMoreContacts: false
      });
      return newState;
    }

    case fromGroupContacts.GroupContactsAcTypes.NEXT_LOAD_GROUP_CONTACTS_FAIL: {
      return {
        ...state,
        isNextPageLoading: false,
        isNextPageLoaded: false,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.UPDATE_GROUP_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.UPDATE_GROUP_CONTACT_SUCCESS: {
      return contactGroupAdapter.updateOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromGroupContacts.GroupContactsAcTypes.UPDATE_GROUP_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.CREATE_GROUP_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.CREATE_GROUP_CONTACT_SUCCESS: {
      return contactGroupAdapter.addOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromGroupContacts.GroupContactsAcTypes.CREATE_GROUP_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.REMOVE_GROUP_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.REMOVE_GROUP_CONTACT_SUCCESS: {
      return contactGroupAdapter.removeOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromGroupContacts.GroupContactsAcTypes.REMOVE_MANY_GROUP_CONTACT_SUCCESS: {
      return contactGroupAdapter.removeMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromGroupContacts.GroupContactsAcTypes.REMOVE_GROUP_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.RESTORE_GROUP_CONTACT: {
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.RESTORE_GROUP_CONTACT_SUCCESS: {
      return contactGroupAdapter.addMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromGroupContacts.GroupContactsAcTypes.RESTORE_GROUP_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.SEARCH_TOGGLE: {
      return  {
        ...state,
        isSearchMode: action.payload,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.START_GROUP_LOADING: {
      return {
        ...state,
        isLoading: true
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.STOP_GROUP_LOADING: {
      return {
        ...state,
        isLoading: false,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.STORE_GROUP_SEARCHPARAMS: {
      return {
        ...state,
        queryParams: action.payload,
      };
    }

    case fromGroupContacts.GroupContactsAcTypes.RESTORE_SAVED_STATE: {
      const savedState = action.payload.contactState;
      return savedState ? { ...state, ...savedState } : state;
    }

    case fromGroupContacts.GroupContactsAcTypes.UPDATE_MANY_GROUP_CONTACT_SUCCESS: {
      return contactGroupAdapter.updateMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

  }
  return state;
}

export const _getIsSearchMode = (state: ContactGroupState) => state.isSearchMode;
export const _getSelectedGroupContactsIds = (state: ContactGroupState) => state.selectedContactsIds;
export const _getIsGroupContactsLoading = (state: ContactGroupState) => state.isLoading;
export const _getIsGroupContactsLoaded = (state: ContactGroupState) => state.isLoaded;
export const _getCurrentGroupOffset = (state: ContactGroupState) => state.currentOffset;
export const _getIsGroupNextPageLoading = (state: ContactGroupState) => state.isNextPageLoading;
export const _getIsGroupNextPageLoaded = (state: ContactGroupState) => state.isNextPageLoaded;
export const _getGroupSearchParams = (state: ContactGroupState) => state.queryParams;
export const _getIsGroupMoreContacts = (state: ContactGroupState) => state.isMoreContacts;
